import { useState, useEffect, useCallback, useRef } from 'react';
import { api, GuildBanResponse } from '@/lib/api';

interface UseGuildBansReturn {
  bans: GuildBanResponse[];
  loading: boolean;
  error: string | null;
  refresh: () => void;
  unban: (banId: string) => Promise<void>;
}

export function useGuildBans(guildId: string | undefined): UseGuildBansReturn {
  const [bans, setBans] = useState<GuildBanResponse[]>([]);
  const [loading, setLoading] = useState(!!guildId);
  const [error, setError] = useState<string | null>(null);
  const fetchIdRef = useRef(0);

  const fetchBans = useCallback(async () => {
    if (!guildId) return;
    const id = ++fetchIdRef.current;
    try {
      setLoading(true);
      setError(null);
      const result = await api.listGuildBans(guildId);
      if (fetchIdRef.current !== id) return;
      setBans(result.bans);
    } catch (err) {
      if (fetchIdRef.current !== id) return;
      setError(err instanceof Error ? err.message : 'Failed to load bans');
    } finally {
      if (fetchIdRef.current === id) {
        setLoading(false);
      }
    }
  }, [guildId]);

  const unban = useCallback(
    async (banId: string) => {
      if (!guildId) return;
      await api.unbanGuildMember(guildId, banId);
      await fetchBans();
    },
    [guildId, fetchBans],
  );

  useEffect(() => {
    fetchBans();
    return () => {
      fetchIdRef.current++;
    };
  }, [fetchBans]);

  return {
    bans,
    loading,
    error,
    refresh: fetchBans,
    unban,
  };
}
